function findByLabel(text) {
	const labels = document.querySelectorAll('label, .MuiFormLabel-root, .MuiInputLabel-root')
	for (const label of labels) {
		if (label.textContent.toLowerCase().includes(text.toLowerCase())) {
			return label.closest('.MuiFormControl-root') || label.closest('.MuiGrid-item') || label.parentElement
		}
	}
	return null
}

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findButton(text, exclude) {
	const buttons = document.querySelectorAll('button')
	for (const btn of buttons) {
		const content = btn.textContent.trim().toLowerCase()
		if (content.includes(text.toLowerCase()) && (!exclude || !content.includes(exclude.toLowerCase()))) {
			return btn
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'magnifying-glass-chart',
		title: ES ? 'Bienvenido a Búsquedas en Tiempo Real' : 'Welcome to Real-Time Searches',
		description: ES
			? 'Lanza búsquedas de precios competitivos en más de 153 webs de alquiler de coches simultáneamente. Configura los parámetros de búsqueda, pulsa buscar y observa cómo los resultados llegan en directo con un mapa de calor de precios por colores.'
			: 'Launch competitive price searches across 153+ car rental websites simultaneously. Configure your search parameters, hit search, and watch results stream in live with a color-coded price heat map.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Show Dropoff')
			return el?.closest('.MuiGrid-item') || el?.parentElement
		},
		icon: 'toggle-on',
		title: ES ? 'Alquileres de solo ida' : 'One-Way Rentals',
		description: ES
			? 'Activa este interruptor cuando el cliente devuelva el coche en una ubicación diferente. Aparecerá un segundo selector de oficina para el lugar de devolución.'
			: 'Enable this toggle when the customer returns the car at a different location. A second office selector will appear for the dropoff location.',
		example: ES
			? 'Ej. Recogida en Madrid → Devolución en Barcelona'
			: 'e.g. Pick up in Madrid → Drop off in Barcelona',
	},
	{
		type: 'field',
		findTarget: () => findByLabel('Pickup') || findByText('All offices')?.closest('.MuiFormControl-root'),
		icon: 'city',
		title: ES ? 'Oficina de recogida' : 'Pickup Office',
		description: ES
			? 'Busca y selecciona el lugar de recogida. Puedes buscar por código de aeropuerto (IATA), nombre de ciudad o nombre de oficina. Este campo es obligatorio.'
			: 'Search and select the pickup location. You can search by airport code (IATA), city name, or office name. This is a required field.',
		example: ES
			? 'Aeropuerto de Madrid Barajas - MAD'
			: 'Madrid Barajas Airport - MAD',
	},
	{
		type: 'field',
		findTarget: () => findByLabel('Sites') || findByText('All Sites')?.closest('.MuiFormControl-root'),
		icon: 'globe-pointer',
		title: ES ? 'Webs a comparar' : 'Websites to Compare',
		description: ES
			? 'Selecciona qué webs de alquiler y metabuscadores consultar. Se permite selección múltiple: elige competidores específicos o selecciona todos para un análisis completo del mercado.'
			: 'Select which rental websites and metasearch engines to query. Multi-select supported — choose specific competitors or select all for a full market scan.',
		example: 'Kayak, Rentalcars, Sixt, Europcar, Booking...',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Pickup time')
			if (!el) return null
			return el.closest('.MuiGrid-item')?.parentElement || el.parentElement
		},
		icon: 'clock-rotate-left',
		title: ES ? 'Hora de recogida y devolución' : 'Pickup & Dropoff Time',
		description: ES
			? 'Establece las horas de recogida y devolución. Algunos proveedores aplican recargos en horarios de madrugada o noche, especialmente en aeropuertos.'
			: 'Set pickup and return hours. Some providers apply surcharges for early morning or late night times, especially at airports.',
		example: ES
			? 'Recogida 10:00 — Devolución 10:00'
			: 'Pickup 10:00 — Dropoff 10:00',
	},
	{
		type: 'field',
		findTarget: () => findByLabel('Rental dates') || findByText('Select rental dates')?.closest('.MuiFormControl-root'),
		icon: 'calendar-days',
		title: ES ? 'Fechas de alquiler' : 'Rental Dates',
		description: ES
			? 'Elige una o varias fechas futuras de recogida. Seleccionar varias fechas te permite detectar tendencias de precios en distintos periodos. Solo se pueden seleccionar fechas futuras.'
			: 'Choose one or more future pickup dates. Multiple dates let you spot pricing trends across different travel periods. Only future dates are selectable.',
		example: ES
			? '15 Mar, 22 Mar, 1 Abr'
			: 'Mar 15, Mar 22, Apr 1',
	},
	{
		type: 'field',
		findTarget: () => findByLabel('Rental length') || findByText('Select rental length')?.closest('.MuiFormControl-root'),
		icon: 'calendar-clock',
		title: ES ? 'Duración del alquiler (LOR)' : 'Length of Rental (LOR)',
		description: ES
			? 'Añade duraciones de alquiler en días. Cada combinación de Fecha × LOR crea una tarea de búsqueda. Máximo 10 combinaciones por búsqueda para mantener la rapidez.'
			: 'Add rental durations in days. Each Date × LOR combination creates one search task. Maximum 10 combinations per search to keep it fast.',
		example: ES
			? '3 días, 5 días, 7 días → 3 fechas × 3 LOR = 9 tareas'
			: '3 days, 5 days, 7 days → 3 dates × 3 LORs = 9 tasks',
	},
	{
		type: 'field',
		findTarget: () => findByLabel('Currency') || findByText('EUR')?.closest('.MuiFormControl-root'),
		icon: 'money-bill-1-wave',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'Normaliza todos los precios a una única moneda para una comparación justa entre proveedores y mercados.'
			: 'Normalize all prices to a single currency for fair, apples-to-apples comparison across providers and markets.',
		example: ES
			? 'EUR (por defecto), USD, GBP'
			: 'EUR (default), USD, GBP',
	},
	{
		type: 'field',
		findTarget: () => findButton('search', 'stop'),
		icon: 'magnifying-glass',
		title: ES ? 'Lanzar búsqueda' : 'Launch Search',
		description: ES
			? 'Haz clic para enviar consultas en paralelo a todas las webs seleccionadas. Los resultados llegan en directo — verás una barra de progreso con las tareas completadas. También puedes detener una búsqueda en curso.'
			: 'Click to dispatch parallel queries to all selected websites. Results stream in live — you\'ll see a progress bar tracking completed tasks. You can also stop a search mid-flight.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Real Time Searches:')
			if (el) {
				const paper = el.closest('.MuiPaper-root')
				if (paper) return paper
			}
			const cards = document.querySelectorAll('.MuiCard-root, .MuiPaper-root')
			for (const card of cards) {
				const rows = card.querySelectorAll('table tbody tr')
				if (rows.length > 1 && !card.textContent.includes('no matching records')) return card
			}
			return null
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de resultados' : 'Results Table',
		description: ES
			? 'Los resultados de la búsqueda aparecen aquí ordenados por precio. Cada fila muestra la web, fecha de recogida, LOR, modelo de coche, código ACRISS, transmisión, categoría, kilometraje, proveedor, método de pago y precio. Los precios están codificados por colores con un mapa de calor.'
			: 'Search results appear here sorted by price. Each row shows the website, pickup date, LOR, car model, ACRISS code, transmission, category, mileage, provider, payment method, and price. Prices are color-coded with a heat map.',
		example: ES
			? 'Verde = tercio más barato | Naranja = rango medio | Rojo = tercio más caro'
			: 'Green = cheapest third | Orange = mid range | Red = most expensive third',
	},
	{
		type: 'field',
		findTarget: () => {
			const tables = document.querySelectorAll('table')
			for (const table of tables) {
				const th = table.querySelector('th')
				if (th && th.textContent.toLowerCase().includes('website')) return th
			}
			return null
		},
		skipIf: () => !document.querySelector('table th'),
		icon: 'earth-americas',
		title: ES ? 'Columna de web' : 'Website Column',
		description: ES
			? 'Muestra el nombre de la web de alquiler con su logotipo y bandera del país. Cada fila incluye iconos de acción rápida: un enlace para abrir el resultado original en la web y un botón de captura de pantalla para ver la página capturada.'
			: 'Shows the rental website name with its logo and country flag. Each row includes quick-action icons: a link to open the original search result on the website, and a screenshot button to view the captured page.',
	},
	{
		type: 'field',
		findTarget: () => {
			const tables = document.querySelectorAll('table')
			for (const table of tables) {
				const ths = table.querySelectorAll('th')
				for (const th of ths) {
					if (th.textContent.trim().toLowerCase() === 'price') return th
				}
			}
			return null
		},
		skipIf: () => {
			const ths = document.querySelectorAll('table th')
			for (const th of ths) {
				if (th.textContent.trim().toLowerCase() === 'price') return false
			}
			return true
		},
		icon: 'fire-flame-curved',
		title: ES ? 'Mapa de calor de precios' : 'Price Heat Map',
		description: ES
			? 'Los precios se ordenan y dividen en tercios. El tercio más barato es verde, el rango medio es naranja y el tercio más caro es rojo. La intensidad del color aumenta hacia los extremos, facilitando identificar las mejores y peores ofertas de un vistazo.'
			: 'Prices are sorted and divided into thirds. The cheapest third is green, the middle range is orange, and the most expensive third is red. Color intensity increases towards the extremes, making it easy to spot the best and worst deals at a glance.',
	},
	{
		type: 'field',
		findTarget: () => {
			const tables = document.querySelectorAll('table')
			for (const table of tables) {
				const ths = table.querySelectorAll('th')
				for (const th of ths) {
					if (th.textContent.trim().toLowerCase().includes('car model')) return th
				}
			}
			return null
		},
		skipIf: () => {
			const ths = document.querySelectorAll('table th')
			for (const th of ths) {
				if (th.textContent.trim().toLowerCase().includes('car model')) return false
			}
			return true
		},
		icon: 'filter',
		title: ES ? 'Filtros por columna' : 'Column Filters',
		description: ES
			? 'Cada columna tiene su propio filtro para refinar resultados. Usa los dropdowns en las cabeceras para filtrar por ACRISS, transmisión, categoría, kilometraje, proveedor o método de pago. El modelo de coche permite búsqueda por texto libre.'
			: 'Each column has its own filter to refine results. Use the dropdowns in the headers to filter by ACRISS, transmission, category, mileage, provider, or payment method. Car model supports free-text search.',
		example: ES
			? 'Ej. Categoría: Economy | Transmisión: Automatic | Proveedor: Europcar'
			: 'e.g. Category: Economy | Transmission: Automatic | Provider: Europcar',
	},
	{
		type: 'field',
		findTarget: () => {
			const btn = document.querySelector('[data-testid="ViewColumnIcon"]')
				|| document.querySelector('button[aria-label*="View Columns"]')
			if (btn) return btn.closest('button') || btn
			const toolbar = document.querySelector('.MuiToolbar-root')
			if (toolbar) {
				const buttons = toolbar.querySelectorAll('button')
				for (const b of buttons) {
					if (b.querySelector('svg') && b.getAttribute('aria-label')?.toLowerCase().includes('column')) return b
				}
			}
			return null
		},
		skipIf: () => !document.querySelector('table th'),
		icon: 'table-columns',
		title: ES ? 'Columnas ocultas' : 'Hidden Columns',
		description: ES
			? 'La tabla tiene columnas adicionales ocultas por defecto: asientos, puertas, rating, posición, en terminal, oferta especial, maletas grandes/pequeñas, proveedor web, kilometraje incluido, política de combustible y seguro. Actívalas desde el botón de columnas en la barra de herramientas.'
			: 'The table has additional columns hidden by default: seats, doors, rating, position, in terminal, special offer, big/small suitcases, web supplier, mileage included, fuel policy, and insurance. Enable them from the columns button in the toolbar.',
	},
	{
		type: 'field',
		findTarget: () => {
			const icon = document.querySelector('.fa-tally') || document.querySelector('[data-icon="tally"]')
			if (icon) {
				const btn = icon.closest('button')
				if (btn?.parentElement) return btn.parentElement
				return btn
			}
			return null
		},
		skipIf: () => !document.querySelector('.fa-tally') && !document.querySelector('[data-icon="tally"]'),
		icon: 'chart-mixed',
		title: ES ? 'Acceso directo a analizadores' : 'Analyzer Quick Access',
		description: ES
			? 'Desde la barra de herramientas puedes abrir directamente los analizadores con los datos de esta búsqueda precargados: LOR Analyzer, DOR Analyzer, Brokers Analyzer, Rating Analyzer y Direct Site Analyzer. Cada uno aparece según los permisos de tu cuenta.'
			: 'From the toolbar you can open analyzers directly with this search\'s data preloaded: LOR Analyzer, DOR Analyzer, Brokers Analyzer, Rating Analyzer, and Direct Site Analyzer. Each one appears based on your account permissions.',
	},
	{
		type: 'field',
		findTarget: () => {
			const icon = document.querySelector('.fa-cloud-arrow-down') || document.querySelector('[data-icon="cloud-arrow-down"]')
				|| document.querySelector('.fa-file-arrow-down') || document.querySelector('[data-icon="file-arrow-down"]')
			if (icon) {
				const btn = icon.closest('button')
				if (btn?.parentElement) return btn.parentElement
				return btn
			}
			return null
		},
		skipIf: () => {
			return !document.querySelector('.fa-cloud-arrow-down') && !document.querySelector('[data-icon="cloud-arrow-down"]')
				&& !document.querySelector('.fa-file-arrow-down') && !document.querySelector('[data-icon="file-arrow-down"]')
		},
		icon: 'file-export',
		title: ES ? 'Generar y exportar búsqueda' : 'Generate & Export Search',
		description: ES
			? 'Dos acciones de exportación disponibles: "Generate Search" crea un registro permanente de la búsqueda con todos sus resultados. "Export Search" descarga los resultados en un archivo. También puedes descargar coincidencias de categorías y ACRISS personalizados como Excel.'
			: 'Two export actions available: "Generate Search" creates a permanent record of the search with all its results. "Export Search" downloads the results as a file. You can also download custom category and ACRISS matches as Excel files.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Cada búsqueda genera una URL única que puedes compartir. Encuentra búsquedas anteriores en la pestaña Historial, o usa la barra de herramientas para acceder a los Analizadores y obtener análisis competitivos más profundos como comparaciones de Sitio Directo o Brokers.'
			: 'Every search generates a unique shareable URL. Find past searches in the History tab, or use the toolbar to jump into Analyzers for deeper competitive insights like Direct Site or Broker comparisons.',
	},
]
